export enum TimelineItemType {
  AUDIO = 'audio',
  ITEM = 'item',
  BACKGROUND = 'background',
  SUBTITLE = 'subtitle',
}

/**
 * Represents a selected timeline item.
 * Uses discriminated union pattern to ensure only one item is selected at a time.
 * Easily extensible for new timeline item types.
 */
export interface SelectedTimelineItem {
  type: TimelineItemType;
  id: string;
}

export interface TimelineItemData {
  id: string;
  type: TimelineItemType;
  startTime: number;
  endTime: number;
  subRow: number;
}

/**
 * Discriminated union for the action being performed on a timeline item.
 * - 'dragging': Item is being moved horizontally
 * - 'resizing': Item duration is being changed via resize handles
 */
export type ItemModifyingAction = {type: 'dragging'} | {type: 'resizing'; resizeHandle: 'left' | 'right'};

/**
 * State representing an item currently being modified (dragged or resized).
 * Stored in Redux to enable:
 * - Single source of truth for drag/resize state
 * - Real-time visual updates during drag/resize
 * - Adjacent page shift calculations during page resize
 */
export interface ItemModifyingState {
  /** ID of the item being modified */
  itemId: string;
  /** The action being performed (dragging or resizing) */
  action: ItemModifyingAction;
  /** Current start time during modification (may differ from committed value) */
  startTime: number;
  /** Current end time during modification (may differ from committed value) */
  endTime: number;
  /** Type of timeline item being modified */
  timelineItemType: TimelineItemType;
  /** Row the item would land on during vertical drag */
  targetSubRow?: number;
  /** Gap index for new row creation during vertical drag */
  insertionIndex?: number;
  /** Starting row before drag began (for revert) */
  originalSubRow?: number;
  /** Raw vertical pixel offset from drag start for free Y-axis movement */
  dragDeltaY?: number;
}

export const TIMELINE_ITEM_DATA_ATTRIBUTE = 'data-timeline-item';
export const TIMELINE_RULER_DATA_ATTRIBUTE = 'data-timeline-ruler';
export const TIMELINE_CONTAINER_DATA_ATTRIBUTE = 'data-timeline-container';
export const POSTER_WHITEBOARD_CONTAINER_ID = 'poster-whiteboard-container';

export const DEFAULT_PIXELS_PER_SECOND = 80;
export const MIN_PIXELS_PER_SECOND = 20;
export const MAX_PIXELS_PER_SECOND = 200;
export const PAGE_TRACK_HEIGHT = 56;
export const ITEM_TRACK_HEIGHT = 32;
export const SUBTITLE_TRACK_HEIGHT = ITEM_TRACK_HEIGHT;
export const TRACK_GAP = 8;
export const HANDLE_WIDTH = 12;
export const TIMELINE_HORIZONTAL_PADDING = 12;
export const RULER_HEIGHT = 24;
export const SUBROW_OFFSET = PAGE_TRACK_HEIGHT + TRACK_GAP;
export const TIMELINE_RIGHT_MARGIN = 64;
/** Pixel distance within which an item edge snaps to another item's edge */
export const SNAP_THRESHOLD_PX = 3;
/** Total visual gap (px) opened between adjacent backgrounds during a reorder drag, sized to fit the indicator + 4px margin on each side */
export const BACKGROUND_DROP_INDICATOR_GAP_PX = 10;

/** Item rows (text/image/video/slideshow tracks) are temporarily hidden on both the desktop and mobile timelines. Flip to true to re-enable everywhere. */
export const SHOW_ITEM_TIMELINE_TRACK = false;
